import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useAclModuleList, useCreateAclSetting } from '../api';
import { AclPermissionMatrix } from '../components/AclPermissionMatrix';
import {
  buildModuleOperation,
  defaultPermissionsFor,
  withParsedChildren,
  type PermissionFlags,
  type PermissionsMap,
} from '../helpers';

export function CreateAclSettingPage() {
  const navigate = useNavigate();
  const userId = Number(useAuthStore((s) => s.userId));
  const moduleList = useAclModuleList();
  const create = useCreateAclSetting();

  const modules = useMemo(
    () => withParsedChildren(moduleList.data ?? []),
    [moduleList.data],
  );

  const [aclName, setAclName] = useState('');
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Seed permissions with defaults once modules load. The ref-equality
  // check on `permissions` keys avoids re-seeding after the user has
  // started editing.
  useEffect(() => {
    if (modules.length && Object.keys(permissions).length === 0) {
      setPermissions(defaultPermissionsFor(modules));
    }
  }, [modules, permissions]);

  const handleToggle = (
    id: string,
    key: keyof PermissionFlags,
    next: boolean,
  ) => {
    setPermissions((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? {}), [key]: next },
    }));
  };

  const handleToggleAll = (
    id: string,
    keys: readonly (keyof PermissionFlags)[],
    next: boolean,
  ) => {
    setPermissions((prev) => {
      const cur = { ...(prev[id] ?? {}) };
      for (const k of keys) cur[k] = next;
      return { ...prev, [id]: cur };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aclName.trim()) {
      toast.error('Role name is required.');
      return;
    }
    create.mutate(
      {
        aclName: aclName.trim(),
        userId,
        moduleOperation: buildModuleOperation(modules, permissions),
        // Multi-select listing scoping (CLI/Groups/Locations/Dialers item
        // filtering) lives in the legacy app. Empty object preserves the
        // wire contract; richer scoping can be added in a v2.
        moduleListingData: '{}',
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Role created');
          navigate('/acl-settings');
        },
        onError: () => toast.error('Could not create the role.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="New Role"
        description="Bundle a set of module permissions into a named role you can assign to sub-super-admins."
        actions={
          <Button asChild variant="outline">
            <Link to="/acl-settings">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Role details</CardTitle>
            <CardDescription>
              Pick a short, descriptive name. Users will see this in the role
              dropdown.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-1.5 sm:max-w-md">
              <Label htmlFor="aclName">
                Role name<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="aclName"
                value={aclName}
                onChange={(e) => setAclName(e.target.value)}
                placeholder="e.g. Campaign Reviewer"
                autoFocus
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
            <CardDescription>
              Toggle which actions this role can perform per module. Parent
              rows summarize visibility; children expose granular CRUD.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AclPermissionMatrix
              modules={modules}
              permissions={permissions}
              expanded={expanded}
              isLoading={moduleList.isLoading}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
              onExpand={(id, next) =>
                setExpanded((p) => ({ ...p, [id]: next }))
              }
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Create role
          </Button>
          <Button asChild type="button" variant="ghost">
            <Link to="/acl-settings">Cancel</Link>
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
}
